<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { normalizeText } from '@/domain/pos/search-normalize';
  import type {
    IMoveTarget,
    TMoveTargetLimit,
  } from '@/domain/catalog/move-targets';

  /**
   * A category picker for dialogs (move products, delete a category): the
   * categories as radio rows with their product count and what limits them,
   * a search from 12 of them, and an amber note when the chosen one limits
   * who can order its products.
   */
  const props = defineProps<{
    targets: readonly IMoveTarget[];
    value?: number | null;
    branchName: string;
    /** Accessible name of the radio group. */
    label: string;
  }>();

  const emit = defineEmits<{
    (e: 'input', categoryId: number): void;
    /** A double click: choose and confirm. */
    (e: 'pick', categoryId: number): void;
  }>();

  const SEARCH_FROM = 12;

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const query = ref('');
  const searchable = computed(() => props.targets.length >= SEARCH_FROM);

  const shown = computed(() => {
    const text = normalizeText(query.value);
    if (!text) return props.targets;
    return props.targets.filter((target) =>
      normalizeText(target.name).includes(text)
    );
  });

  const selected = computed(
    () => props.targets.find((target) => target.id === props.value) ?? null
  );

  const LIMIT_BADGES: Record<TMoveTargetLimit, string> = {
    hidden: 'menuPage.move.badge.hidden',
    hiddenHere: 'menuPage.move.badge.hiddenHere',
    posOnly: 'menuPage.move.badge.posOnly',
    scheduled: 'menuPage.move.badge.scheduled',
  };

  const LIMIT_NOTES: Record<TMoveTargetLimit, string> = {
    hidden: 'menuPage.move.note.hidden',
    hiddenHere: 'menuPage.move.note.hiddenHere',
    posOnly: 'menuPage.move.note.posOnly',
    scheduled: 'menuPage.move.note.scheduled',
  };

  function badge(limit: TMoveTargetLimit): string {
    return t(LIMIT_BADGES[limit], { branch: props.branchName });
  }

  const note = computed(() =>
    selected.value?.limit
      ? t(LIMIT_NOTES[selected.value.limit], {
          name: selected.value.name,
          branch: props.branchName,
          schedule: selected.value.schedule ?? '',
        })
      : null
  );
</script>

<template>
  <div class="v2-catalog-target-list">
    <PosUiInput
      v-if="searchable"
      v-model="query"
      class="target-search"
      icon="magnify"
      clearable
      :placeholder="$t('menuPage.move.search')"
      :aria-label="$t('menuPage.move.search')"
    />

    <div class="target-options" role="group" :aria-label="label">
      <button
        v-for="target in shown"
        :key="target.id"
        type="button"
        class="target-option"
        :class="{ 'is-selected': target.id === value }"
        :aria-pressed="target.id === value ? 'true' : 'false'"
        @click="emit('input', target.id)"
        @dblclick="emit('pick', target.id)"
      >
        <span class="option-radio" />
        <span class="option-name" :title="target.name">{{ target.name }}</span>
        <span v-if="target.limit" class="option-badge">{{
          badge(target.limit)
        }}</span>
        <span class="option-count">{{ target.count }}</span>
      </button>
      <p v-if="!shown.length" class="target-empty">
        {{ $t('menuPage.move.noResults') }}
      </p>
    </div>

    <p v-if="note" class="target-note" role="status">
      <i class="mdi mdi-alert-outline note-icon" />
      <span>{{ note }}</span>
    </p>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-target-list {
    @apply tw-flex tw-min-h-0 tw-flex-col;

    .target-search {
      @apply tw-mb-3;
    }

    .target-options {
      @apply tw-min-h-0 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200;
      max-height: 320px;
    }

    .target-option {
      @apply tw-flex tw-min-h-[48px] tw-w-full tw-cursor-pointer tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-bg-surface tw-px-3 tw-text-left tw-text-sm tw-text-neutral-800 tw-transition-colors tw-duration-150;

      &:last-child {
        @apply tw-border-b-0;
      }

      &:hover {
        @apply tw-bg-neutral-50;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }

      .option-radio {
        @apply tw-mr-3 tw-h-[18px] tw-w-[18px] tw-flex-none tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-300 tw-bg-surface;
      }

      &.is-selected {
        @apply tw-bg-neutral-50;

        .option-radio {
          @apply tw-border-neutral-900 tw-bg-neutral-900;
          box-shadow: inset 0 0 0 3px rgb(var(--c-surface));
        }
      }

      .option-name {
        @apply tw-min-w-0 tw-flex-1 tw-truncate tw-font-medium;
      }

      .option-badge {
        @apply tw-ml-2 tw-flex-none tw-whitespace-nowrap tw-rounded-full tw-bg-neutral-100 tw-px-2 tw-py-0.5 tw-text-xs tw-text-neutral-600;
      }

      .option-count {
        @apply tw-ml-3 tw-flex-none tw-tabular-nums tw-text-neutral-500;
      }
    }

    .target-empty {
      @apply tw-m-0 tw-px-3 tw-py-4 tw-text-sm tw-text-neutral-500;
    }

    .target-note {
      @apply tw-mb-0 tw-mt-3 tw-flex tw-items-start tw-rounded-lg tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-text-amber-900;

      .note-icon {
        @apply tw-mr-2 tw-flex-none tw-text-base tw-leading-5 tw-text-amber-600;
      }
    }
  }
</style>
